import React, { ReactNode } from 'react';
import Helmet from "react-helmet";
import classNames from 'classnames';
import { Link } from 'react-router-dom';
import { HashLink } from 'react-router-hash-link';

import { withRouter } from 'react-router-dom';
import { RouteComponentProps } from 'react-router';
// model
import Data from '../../model/Data';
import CityInterface from '../../model/City';
import AreaInterface from '../../model/Area';
import VacancyInterface from '../../model/Vacancy';
// common components
import Grid from '../../common/components/Grid/Grid';
import Select from '../../common/components/Select/Select';
import Breadcrumbs from '../../common/components/Breadcrumbs/Breadcrumbs';
// local
import AreaBlock from './components/AreaBlock/AreaBlock';
import SpecialVacancy from './components/SpecialVacancy/SpecialVacancy';
// analytics
import gtag from '../../common/components/analytics/gtag.js';

// styles
import './Catalog.css';
import './Catalog320.css';
import './Catalog768.css';
import './Catalog1024.css';
import './Catalog1280.css';
import './Catalog1376.css';
import './Catalog1920.css';
// helpers
import ResizeHelper from '../../common/helpers/resize';
import useYandexRotor from "../../common/helpers/useYandexRotor";

interface RouterProps {
  city: string;
  area: string;
}

interface CatalogState {
  selectedCity: CityInterface,
  selectedArea: string | null,
  specialVacancyOpened: boolean
}

type CatalogProps = RouteComponentProps<RouterProps>;

class Catalog extends React.Component<CatalogProps, CatalogState> {

  private windowWidth: number;

  private cities = Data.Instance.citiesWithVacancies;

  private vacancies: Array<VacancyInterface>;

  constructor(props: CatalogProps) {
    super(props);
    const uriParams: RouterProps = props.match.params;
    this.state = {
      selectedArea: uriParams.area || null,
      selectedCity: uriParams.city ? Data.Instance.cityByAlias(uriParams.city) : Data.Instance.cities[0],
      specialVacancyOpened: false,
    };
    this.vacancies = Data.Instance.vacancies({city: this.state.selectedCity.id})
    this.onCityChange = this.onCityChange.bind(this);
    this.onSpecialVacancyClick = this.onSpecialVacancyClick.bind(this);
    this.handleScroll = this.handleScroll.bind(this);
    this.isMobile = this.isMobile.bind(this);
    this.scrollWithOffset = this.scrollWithOffset.bind(this);
    this.windowWidth = 0;
    ResizeHelper.Instance.addCallback(this.refresh.bind(this));
  }

  componentDidMount() {
    if (this.vacancies.length < 1) {
      this.props.history.replace('/');
    }
    this.windowWidth = window.innerWidth;
    window.addEventListener('scroll', this.handleScroll);
  }

  componentWillUnmount() {
    window.removeEventListener('scroll', this.handleScroll);
  }

  handleScroll(event: Event) {
    const breadcrumbs = window.document.getElementsByClassName("breadcrumbs")[0];
    const target: Window = event.currentTarget as Window;

    if (this.windowWidth < 1023 && target && breadcrumbs) {
      const scrollTop = target.scrollY;
      const areasBlock = window.document.getElementById("areas");

      if (areasBlock) {
        if (scrollTop > 0) {
          areasBlock.classList.add("sticky");
          (breadcrumbs as HTMLElement).style.paddingBottom = `${areasBlock.clientHeight}px`;
        } else {
          areasBlock.classList.remove("sticky");
          (breadcrumbs as HTMLElement).style.paddingBottom = ``;
        }
      }
    }
  }

  refresh(): void {
    this.windowWidth = window.innerWidth;
    this.setState((prevState) => ({...prevState}))
  }

  onAreaChange(area: string): void {
    this.setState((prevState) => ({ ...prevState, selectedArea: area }));
    this.pushHistory(this.state.selectedCity.uri_alias, area);
  }

  onCityChange(cityId: string): void {
    const city = Data.Instance.cityById(cityId);
    this.vacancies = Data.Instance.vacancies({city: city.id})
    this.setState((prevState) => ({ ...prevState, selectedCity: city }));
    this.pushHistory(city.uri_alias);
  }

  onSpecialVacancyClick(ev: React.MouseEvent<HTMLAnchorElement>): void {
    ev.preventDefault();
    ev.stopPropagation();
    const currentTarget = ev.currentTarget;

    // GoogleAnalytics event
    gtag('event', 'show', {
      'event_category': 'vacancy_form',
      'event_label': 'form_vacancy_empty'
    });

    this.setState(
      (prevState) => ({ ...prevState, specialVacancyOpened: true }),
      () => {
        if (currentTarget) this.moveToBlock(currentTarget.getAttribute('data-anchor'));
      }
    );
  }

  moveToBlock(id: string | null) {
    if (id && /^#/.test(id)) {
      setTimeout(() => {
        const toScroll = document.getElementById(id.substring(1));
        if (toScroll) {
          this.scrollWithOffset(toScroll);
        }
      }, 250);
    }
  }

  isMobile = () => {
    const MOBILE_BREAKPOINT = 1024;
    return this.windowWidth < MOBILE_BREAKPOINT;
  };

  scrollWithOffset = (el: HTMLElement) => {
    // const breadcrumbs = window.document.getElementsByClassName('breadcrumbs')[0];
    function getExistingElement(selector: string) {
      const query = document.querySelectorAll(selector);
      for (let i = 0; i < query.length; i++) {
        if (query[i].clientHeight > 0) return query[i];
      }
      return document.querySelector(selector);
    }

    const leftBar = getExistingElement('.areas');
    const offset = this.isMobile() ? leftBar!.clientHeight : 0;
    const pos = getExistingElement(`#${el.id}`)!.getBoundingClientRect();

    window.scroll({
      top: pos.top + window.scrollY - offset,
      left: 0,
      behavior: "smooth"
    });
  };

  // eslint-disable-next-line class-methods-use-this
  areaView(a: AreaInterface): ReactNode {
    const { selectedCity } = this.state;
    const className: {[key: string]: boolean} = { "left-bar-link": true }
    // TODO: find better way to add vacancies count
    const len = Data.Instance.vacancies({ city: selectedCity.id, area: a.id }).length;

    if (a.id === this.state.selectedArea) className["active"] = true;

    return (
      <HashLink
        key={a.id}
        scroll={this.scrollWithOffset}
        className={classNames(className)}
        to={this.catalogLink(this.state.selectedCity.uri_alias, a.uri_alias)}>
          {a.name}
          <span className="count">{len}</span>
      </HashLink>
    );
  }

  // eslint-disable-next-line class-methods-use-this
  areaBlocks(city: CityInterface, itemClass: string): ReactNode {
    return Data.Instance.areasByCity(city.id).map((a) => {
      return (
        <AreaBlock
          key={`${city.id}-${a.id}`}
          itemClass={ itemClass }
          vacancies={ this.vacancies.filter(v => v.area === a.id) }
          city={ city } area={ a }
        />
      );
    });
  }

  private catalogLink(cityAlias: string, areaAlias: string | null = null) {
    return areaAlias ? `/${cityAlias}#${areaAlias}` : `/${cityAlias}`;
  }

  private pushHistory(city: string, area?: string): void {
    this.props.history.replace(this.catalogLink(city, area));
  }

  render(): ReactNode {
    const { selectedCity, specialVacancyOpened } = this.state;
    const breadcrumbs = [
      { text: 'Все вакансии', link: this.catalogLink(selectedCity.uri_alias) },
    ];
    const windowWidth = window.innerWidth;
    let leftbarGridClass;
    let contentGridClass;
    let areaItemGridClass = Grid.itemClass(6);

    if (windowWidth < 767) {
      leftbarGridClass = Grid.itemClass(4);
      contentGridClass = Grid.itemClass(4);
      areaItemGridClass = Grid.itemClass(4);
    } else if (windowWidth >= 768 && windowWidth < 1024) {
      leftbarGridClass = Grid.itemClass(8);
      contentGridClass = Grid.itemClass(8);
      areaItemGridClass = Grid.itemClass(4);
    } else if (windowWidth >= 1024 && windowWidth < 1280) {
      leftbarGridClass = Grid.itemClass(3);
      contentGridClass = Grid.itemClass(5, 3);
      areaItemGridClass = Grid.itemClass(4);
    } else if (windowWidth >= 1280) {
      leftbarGridClass = Grid.itemClass(4);
      contentGridClass = Grid.itemClass(8, 4);
      areaItemGridClass = Grid.itemClass(6);
    }

    useYandexRotor(Data.Instance.isDataLoaded);

    return (
      <div className="catalog">
        <Helmet
          title={`Вакансии ${selectedCity.name_prepositional}`}
        />

        { /* DESKTOP LAYOUT */ }
        <div className="container mobile-hidden tablet-hidden">
          <Grid>
            <div className={`left-bar ${leftbarGridClass}`}>
              <Breadcrumbs brand={Data.Instance.company} items={breadcrumbs} />
              <Select
                className="city-select"
                selected={selectedCity.id}
                options={Data.Instance.citiesAsOptions(this.cities)}
                onChange={this.onCityChange}
              />
              <div id="areas" className="areas">
                { Data.Instance.areasByCity(selectedCity.id).map((a) => this.areaView(a)) }
                <Link
                  key="no-place-for-me"
                  className="left-bar-link special-vacancy-link"
                  to="/"
                  onClick={this.onSpecialVacancyClick}
                  data-anchor="#form-wrapper">
                    Нет вакансии для тебя?
                </Link>
              </div>
            </div>
            <div className={`main-content ${contentGridClass}`}>
              { this.areaBlocks(selectedCity, areaItemGridClass) }
              <div id="form-wrapper" className="area-block no-place-for-me">
                <div className="area-block-head">
                  <div className="title">Нет вакансии<span> для тебя</span>?</div>
                  <div className="count count_infinity"></div>
                </div>
                <div className="area-block-body">
                  <SpecialVacancy opened={specialVacancyOpened}></SpecialVacancy>
                </div>
              </div>
            </div>
          </Grid>
        </div>

        { /* MOBILE LAYOUT */ }
        <div className="mobile-visible tablet-visible hidden">
          <div className={`left-bar`}>
            <div className="container">
              <Breadcrumbs brand={Data.Instance.company} items={breadcrumbs} />
              <Select
                className="city-select"
                selected={selectedCity.id}
                options={Data.Instance.citiesAsOptions(this.cities)}
                onChange={this.onCityChange}
              />
              <div id="areas" className="areas">
                { Data.Instance.areasByCity(selectedCity.id).map((a) => this.areaView(a)) }
                <Link
                  key="no-place-for-me"
                  className="left-bar-link special-vacancy-link"
                  to="/"
                  onClick={this.onSpecialVacancyClick}
                  data-anchor="#form-wrapper">
                    Нет вакансии для тебя?
                </Link>
              </div>
            </div>
          </div>
          <div className={`main-content`}>
            <div className="container">
            { this.areaBlocks(selectedCity, areaItemGridClass) }
              <div id="form-wrapper" className="area-block no-place-for-me">
                <div className="area-block-head">
                  <div className="title">Нет вакансии<span> для тебя</span>?</div>
                  <div className="count count_infinity"></div>
                </div>
                <div className="area-block-body">
                  <SpecialVacancy opened={specialVacancyOpened}></SpecialVacancy>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }
}

export default withRouter(Catalog);
